import collection_data_v5 from './collections-v5.json'
import collection_data_v6 from './collections-v6.json'
import collection_data_v7 from './collections-v7.json'
import iconPackData from 'app/views/icons/packs/iconPackData.json'
import { capitalize } from 'lodash'

function getData(version) {
  switch (version) {
    case 5:
      return collection_data_v5
    case 6:
      return collection_data_v6
    case 7:
      return collection_data_v7
  }
}

export function getCollections(version) {
  return getData(version).iconCollections
}

export function getCollection(version, collectionName) {
  return getData(version).iconCollections.find((collection) => {
    return collection.name === collectionName
  })
}

export function getIconPack(packName, version) {
  const pack = getData(version).iconPacks.find((pack) => {
    return pack.name === packName
  })

  const extraPackData = getExtraPackData(packName)

  return {
    description: pack.description,
    designerName: pack.designerName,
    label: pack.label,
    labelIconName: pack.labelIconName,
    labelIconPrefix: pack.labelIconPrefix,
    name: pack.name,
    sourceCanvasHeight: pack.sourceCanvasHeight,
    sourceGridHeight: pack.sourceGridHeight,
    designerUrl: pack.designerUrl,
    editorial: extraPackData.editorial,
    featuredFamilyStyles: extraPackData.featuredFamilyStyles,
    featuredIcons: extraPackData.featuredIcons,
    heroImage: extraPackData.heroImage,
    iconDesignShowcase: extraPackData.iconDesignShowcase,
    lastUpdatedVersion: extraPackData.lastUpdatedVersion,
    mockups: extraPackData.mockups,
    status: extraPackData.status,
    theme: extraPackData.theme,
    thumbImage: extraPackData.thumbImage,
    familyStyles: getIconFamilyStyles(pack.familyStyles),
    iconCollectionName: getFirstCollectionByIconPack(version, pack.name)
  }
}

export function getIconPacks(version, excludeBrands = false) {
  return getData(version).iconPacks.reduce((acc, pack) => {
    if (!(excludeBrands && pack.name === 'brands')) {
      acc.push(getIconPack(pack.name, version))
    }

    return acc
  }, [])
}

export function getIconPacksByFamily(version, family) {
  return getData(version).iconPacks.filter((pack) => {
    return pack.familyStyles.some((fs) => fs.family === family)
  })
}

export function getIconCollectionsByFamily(version, icon_packs) {
  return getCollections(version).filter((collection) => {
    return collection.iconPacks.some((pack) => {
      return icon_packs.includes(pack)
    })
  })
}

export function getProPlusExclusiveIconPacks(version) {
  // Pro Plus is only available in v7
  if (version !== 7) {
    return []
  }

  const proCollection = getCollection(version, 'pro-collection')
  const proPlusCollection = getCollection(version, 'pro-plus-collection')

  return proPlusCollection.iconPacks.filter((packName) => !proCollection.iconPacks.includes(packName))
}

export function getProPlusExclusiveIconPackNames(version) {
  return getProPlusExclusiveIconPacks(version)
}

export function getProPlusExclusiveIconPackObjects(version) {
  return getProPlusExclusiveIconPacks(version).map((packName) => getIconPack(packName, version))
}

export function getIconPacksByCollection(version, collectionName) {
  const collection = getCollection(version, collectionName)

  return getData(version)
    .iconPacks.filter((pack) => collection.iconPacks.includes(pack.name))
    .reduce((acc, pack) => {
      acc.push(getIconPack(pack.name, version))

      return acc
    }, [])
}

export function getDefaultIconPack(version) {
  return getData(version).iconPacks.find((pack) => pack.name === 'classic')
}

export function getIconPacksByList(version, packList) {
  return getData(version).iconPacks.filter((pack) => packList.includes(pack.name))
}

export function getVisualTags(version) {
  return getData(version).visualTags
}

function getIconFamilyStyles(icons) {
  return icons.reduce((acc, familyStyle) => {
    acc.push({
      family: familyStyle.family,
      style: familyStyle.style,
      visualTags: familyStyle.visualTags,
      selected: false,
      label: `${capitalize(familyStyle.family)} ${capitalize(familyStyle.style)}`
    })

    return acc
  }, [])
}

function getExtraPackData(packName) {
  const data = iconPackData.filter((p) => {
    return p.name === packName
  })[0]

  return data
}

export function getNewV7IconPacks() {
  const v6IconPackNames = new Set(getIconPacks(6).map((pack) => pack.name))

  return getIconPacks(7).filter((pack) => !v6IconPackNames.has(pack.name))
}

export function getIconPacksCount(version) {
  return getData(version).iconPacks.length
}

export function getCollectionByName(version, collectionName) {
  const allCollections = getCollections(version)
  return allCollections?.find((collection) => collection?.name === collectionName) || null
}

function getFirstCollectionByIconPack(version, iconPackName) {
  if (iconPackName === 'classic') {
    return 'pro-collection'
  }

  const allCollections = getCollections(version)

  const firstCollectionFound = allCollections?.find((collection) => collection.iconPacks.includes(iconPackName))

  return firstCollectionFound?.name || null
}
